/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, h } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useState, useEffect } from 'preact/hooks';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import {
  Table,
  Checkbox,
  Picker,
  Price,
  Button,
  Input,
  InLineAlert,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { getUserTimezone } from '@/quote-management/utils/dateUtils';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';

import '@/quote-management/components/ProductListTable/ProductListTable.css';
import { WarningFilled } from '@adobe-commerce/elsie/icons';

export interface ProductListTableProps
  extends HTMLAttributes<HTMLDivElement | HTMLFormElement> {
  items: CartItemModel[];
  canEdit: boolean;
  readOnly?: boolean;
  showActions?: boolean;
  onItemCheckboxChange?: (
    item: CartItemModel,
    isSelected: boolean
  ) => void;
  onItemDropdownChange?: (item: CartItemModel, action: string) => void;
  onQuantityChange?: (item: CartItemModel, newQuantity: number) => void;
  onUpdate?: (e: SubmitEvent) => void;
  dropdownSelections?: Record<string, string | undefined>;
}

export const ProductListTable: FunctionComponent<ProductListTableProps> = ({
  className,
  items,
  canEdit,
  readOnly = false,
  showActions,
  onItemCheckboxChange,
  onItemDropdownChange,
  onQuantityChange,
  onUpdate,
  dropdownSelections,
  ...props
}) => {
  // Track original quantities to detect changes
  const [originalQuantities, setOriginalQuantities] = useState<
    Record<string, number>
  >({});
  const [currentQuantities, setCurrentQuantities] = useState<
    Record<string, number>
  >({});

  // Initialize quantities when items change
  useEffect(() => {
    const quantities: Record<string, number> = {};
    items.forEach((item) => {
      quantities[item.uid] = item.quantity;
    });
    setOriginalQuantities(quantities);
    setCurrentQuantities(quantities);
  }, [items]);

  // Check if any quantities have changed
  const hasQuantityChanges = Object.keys(currentQuantities).some(
    (uid) => currentQuantities[uid] !== originalQuantities[uid]
  );

  const dictionary = useText({
    updateButton: 'NegotiableQuote.Manage.productListTable.submitButton',
    productNameHeader:
      'NegotiableQuote.Manage.productListTable.headers.productName',
    skuHeader: 'NegotiableQuote.Manage.productListTable.headers.sku',
    priceHeader: 'NegotiableQuote.Manage.productListTable.headers.price',
    quantityHeader: 'NegotiableQuote.Manage.productListTable.headers.quantity',
    discountHeader: 'NegotiableQuote.Manage.productListTable.headers.discount',
    subtotalHeader: 'NegotiableQuote.Manage.productListTable.headers.subtotal',
    actionsHeader: 'NegotiableQuote.Manage.productListTable.headers.actions',
    editNoteToSeller:
      'NegotiableQuote.Manage.productListTable.actions.editNoteToSeller',
    remove: 'NegotiableQuote.Manage.productListTable.actions.remove',
    notesHeader: 'NegotiableQuote.Manage.productListTable.notes.header',
    leftANote: 'NegotiableQuote.Manage.productListTable.notes.leftANote',
    buyer: 'NegotiableQuote.Manage.productListTable.notes.buyer',
    seller: 'NegotiableQuote.Manage.productListTable.notes.seller',
    outOfStock: 'NegotiableQuote.Manage.productListTable.outOfStock',
    outOfStockMessage:
      'NegotiableQuote.Manage.productListTable.outOfStockMessage',
  });

  const columns = [
    {
      label: dictionary.productNameHeader,
      key: 'productName',
    },
    {
      label: dictionary.skuHeader,
      key: 'sku',
    },
    {
      label: dictionary.priceHeader,
      key: 'price',
    },
    {
      label: dictionary.quantityHeader,
      key: 'quantity',
    },
    {
      label: dictionary.discountHeader,
      key: 'discount',
    },
    {
      label: dictionary.subtotalHeader,
      key: 'subtotal',
    },
  ];

  // Add checkbox column when canEdit (for bulk operations)
  if (canEdit && !readOnly) {
    columns.unshift({
      label: '',
      key: 'checkbox',
    });
  }

  // Add actions column when showActions is explicitly true, or when canEdit is true and showActions is not explicitly false
  const shouldShowActions = showActions ?? canEdit;
  if (shouldShowActions && !readOnly) {
    columns.push({
      label: dictionary.actionsHeader,
      key: 'actions',
    });
  }

  const handleItemCheckboxChange = (
    event: Event,
    item: CartItemModel
  ) => {
    const isSelected = (event.target as HTMLInputElement).checked;
    onItemCheckboxChange?.(item, isSelected);
  };

  const handleItemDropdownChange = (
    event: Event,
    item: CartItemModel
  ) => {
    const action = (event.target as HTMLSelectElement).value;
    onItemDropdownChange?.(item, action);
  };

  const handleQuantityChange = (event: Event, item: CartItemModel) => {
    const newQuantity = parseInt((event.target as HTMLInputElement).value, 10);
    if (!isNaN(newQuantity) && newQuantity > 0) {
      setCurrentQuantities((prev) => ({
        ...prev,
        [item.uid]: newQuantity,
      }));
      onQuantityChange?.(item, newQuantity);
    }
  };

  const handleUpdate = (event: SubmitEvent) => {
    event.preventDefault();
    onUpdate?.(event);
  };

  const discountElement = (amountOff: number, percentOff: number) => {
    return amountOff > 0 ? (
      <div className="quote-management-product-list-table__discount-container">
        <span className="quote-management-product-list-table__discount-percent">
          {percentOff}%
        </span>
        <span className="quote-management-product-list-table__discount-price">
          (<Price amount={amountOff} />)
        </span>
      </div>
    ) : undefined;
  };

  const getProductNameContent = (item: CartItemModel) => {
    const configurableOptions = item.configurableOptions?.map((option) => (
      <div
        key={option.optionLabel}
        className="quote-management-product-list-table__configurable-option"
      >
        <span className="quote-management-product-list-table__configurable-option-label">
          {option.optionLabel}:
        </span>
        <span className="quote-management-product-list-table__configurable-option-value">
          {option.valueLabel}
        </span>
      </div>
    ));

    const bundleOptions = item.bundleOptions?.map((option) => (
      <div
        key={option.label}
        className="quote-management-product-list-table__bundle-option"
      >
        <span className="quote-management-product-list-table__bundle-option-label">
          {option.label}
        </span>
        <div className="quote-management-product-list-table__bundle-option-values">
          {option.values.map((value) => (
            <span
              key={value.label}
              className="quote-management-product-list-table__bundle-option-value"
            >
              <span className="quote-management-product-list-table__bundle-option-value-quantity">
                {value.quantity} x
              </span>
              <span className="quote-management-product-list-table__bundle-option-value-label">
                {value.label}
              </span>
              <Price
                className="quote-management-product-list-table__bundle-option-value-original-price"
                amount={value.originalPrice.value}
                currency={value.originalPrice.currency}
                weight="normal"
              />
            </span>
          ))}
        </div>
      </div>
    ));

    return (
      <div className="quote-management-product-list-table__product-name-container">
        <span className="quote-management-product-list-table__product-name">
          {item.product.name}
        </span>
        {configurableOptions}
        {bundleOptions}
      </div>
    );
  };

  const formatNoteTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: getUserTimezone(),
    });
  };

  const getNotesContent = (item: CartItemModel) => {
    const validNotesFromBuyer = item.noteFromBuyer?.filter(
      (note) => note && note.note && note.note.trim() !== ''
    ) || [];

    const validNotesFromSeller = item.noteFromSeller?.filter(
      (note) => note && note.note && note.note.trim() !== ''
    ) || [];

    const validNotes = [...validNotesFromBuyer, ...validNotesFromSeller];

    if (validNotes.length === 0) {
      return null;
    }

    return (
      <div
        className="quote-management-product-list-table__notes-container"
        data-testid={`item-notes-${item.product.sku}`}
      >
        <div className="quote-management-product-list-table__notes-header">
          {dictionary.notesHeader}
        </div>
        <div className="quote-management-product-list-table__notes-list">
          {validNotes.map((note, index) => (
            <div
              key={note.noteUid || index}
              className="quote-management-product-list-table__note-item"
            >
              <div className="quote-management-product-list-table__note-content">
                <strong className="quote-management-product-list-table__note-meta">
                  {/* TODO: When backend exposes creator_name for notes, replace (Buyer) with the actual name */}
                  {formatNoteTimestamp(note.createdAt)} ({validNotesFromBuyer.includes(note) ? dictionary.buyer : dictionary.seller})
                </strong>{' '}
                {dictionary.leftANote}
              </div>
              <div className="quote-management-product-list-table__note-text">
                {note.note}{' '}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const expandedRows = new Set<number>();
  const rowData = items.flatMap((item, index) => {
    const notes = getNotesContent(item);
    if (notes || item.outOfStock) {
      expandedRows.add(index);
    }

    const quantityElement = canEdit ? (
      <Input
        className="quote-management-product-list-table__quantity-input"
        type="number"
        min="1"
        value={
          currentQuantities[item.uid]?.toString() || item.quantity.toString()
        }
        onChange={(e) => handleQuantityChange(e, item)}
        disabled={readOnly || !canEdit}
        data-testid={`quantity-input-${item.product.sku}`}
      />
    ) : (
      <span className="quote-management-product-list-table__quantity">
        {item.quantity}
      </span>
    );

    const productRow = {
      checkbox: (
        <Checkbox
          className="quote-management-product-list-table__checkbox"
          name="itemSelected"
          data-testid={`item-checkbox-${item.product.sku}`}
          onChange={(e) => handleItemCheckboxChange(e, item)}
          value={item.product.sku}
        />
      ),
      productName: getProductNameContent(item),
      sku: (
        <span className="quote-management-product-list-table__sku">
          {item.product.sku}
        </span>
      ),
      price: (
        <Price
          className="quote-management-product-list-table__price"
          amount={item.prices.originalItemPrice.value}
          currency={item.prices.originalItemPrice.currency}
        />
      ),
      quantity: quantityElement,
      discount: item.rowCatalogDiscount
        ? discountElement(
          item.rowCatalogDiscount.amountOff,
          item.rowCatalogDiscount.percentOff
        )
        : undefined,
      subtotal: (
        <Price
          className="quote-management-product-list-table__subtotal"
          amount={item.prices.rowTotal.value}
          currency={item.prices.rowTotal.currency}
        />
      ),
      actions: (
        <Picker
          className="quote-management-product-list-table__actions"
          data-testid={`item-dropdown-${item.product.sku}`}
          name={`item-dropdown-${item.product.sku}`}
          handleSelect={(e) => handleItemDropdownChange(e, item)}
          placeholder="Select"
          value={dropdownSelections?.[item.uid] ?? ''}
          options={[
            { text: dictionary.editNoteToSeller, value: 'edit' },
            { text: dictionary.remove, value: 'remove' },
          ]}
        />
      ),
      _rowDetails: <>
        {/* If the item is out of stock, show a message */}
        {item.outOfStock && (
          <div className="quote-management-product-list-table__out-of-stock-message">
            <InLineAlert
              type="warning"
              variant="primary"
              icon={<WarningFilled />}
              heading={dictionary.outOfStock}
              description={dictionary.outOfStockMessage}
            />
          </div>
        )}
        {/* If there are notes, create a notes details */}
        {notes && (
          <div className="quote-management-product-list-table__notes-row-wrapper">
            {notes}
          </div>
        )}
      </>
    };

    return [productRow];
  });

  const table = (
    <Table
      columns={columns}
      rowData={rowData}
      data-testid="product-list-table"
      mobileLayout="stacked"
      expandedRows={expandedRows}
    />
  );

  // if can edit, the wrapper node should use the form element, else use the div element
  const wrapperNode = canEdit ? h('form', {}) : h('div', {});
  const wrapperProps = canEdit
    ? {
      onSubmit: handleUpdate,
      ...props,
    }
    : props;

  const submitButton = (
    <Button
      type="submit"
      disabled={readOnly || !canEdit || !hasQuantityChanges}
      data-testid="product-list-table-submit-button"
    >
      {dictionary.updateButton}
    </Button>
  );

  return (
    <VComponent
      node={wrapperNode}
      className={classes([
        'quote-management-product-list-table-container',
        className,
      ])}
      data-testid="product-list-table-container"
      {...wrapperProps}
    >
      {table}
      <div className="quote-management-product-list-table-container__submit-container">
        {submitButton}
      </div>
    </VComponent>
  );
};
